import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Coins, HeadphonesIcon, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://customer-assets.emergentagent.com/job_jungle-retreat-3/artifacts/xlxaxtfd_Forest%20Retreat.png";

export default function PartnerWithUs() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", inquiry_type: "host" });
  const [busy, setBusy] = useState(false);
  const set = k => e => setF({ ...f, [k]: e.target.value });
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/partner", f);
      toast.success("Received. Our partnerships team will reach out within 24 hours.");
      setF({ name: "", email: "", phone: "", message: "", inquiry_type: "host" });
    } catch { toast.error("Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-ivory">
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900/95 via-forest-900/55 to-forest-900/20" />
        <div className="relative container-tarzan py-20 text-ivory">
          <div className="eyebrow text-sand-200 mb-4">Partner With Us</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-3xl">Grow the collection with us.</h1>
          <p className="mt-6 text-ivory/80 max-w-xl text-base md:text-lg leading-relaxed">Property owners, land partners, hospitality operators, and cultural collaborators — if your work aligns with slow, thoughtful travel in nature, we would love to talk.</p>
        </div>
      </section>

      <section className="py-24">
        <div className="container-tarzan grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { i: Coins, h: "Property Owners", b: "List your home in the Tarzan collection. End-to-end management, curated guests, 70% of net revenue." },
            { i: BadgeCheck, h: "Land Partners", b: "Own 5+ acres of nature-rich land? Build with us on a revenue-share model — zero capex from your side." },
            { i: HeadphonesIcon, h: "Hospitality Operators", b: "F&B, spa, wellness, adventure operators — plug into our on-site guest calendar at every retreat." },
            { i: ShieldCheck, h: "Cultural Collaborators", b: "Artisans, chefs, musicians, guides. Bring your craft to our guests through curated in-retreat sessions." },
          ].map(({i:Ic,h,b}) => (
            <div key={h} className="bg-white border border-forest/10 rounded-3xl p-7 soft-shadow">
              <div className="h-12 w-12 rounded-full bg-forest text-ivory grid place-items-center mb-5"><Ic className="h-5 w-5" strokeWidth={1.5}/></div>
              <div className="font-serif text-xl text-ink">{h}</div>
              <p className="text-ink-soft text-sm mt-3 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-forest-50/60">
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="eyebrow mb-3">How it works</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">From first call to first guest in <em className="not-italic text-forest">~30 days.</em></h2>
            <ol className="mt-10 space-y-6">
              {[
                ["Introductions", "We talk about your vision, location, and how you'd like to partner."],
                ["Site visit", "A member of our team visits, stays a night, and shares honest feedback."],
                ["Onboarding", "We photograph, brand, list, price, and brief the on-site team."],
                ["Going live", "Your property or service launches across Tarzan's channels."],
              ].map(([h, b], i) => (
                <li key={h} className="flex gap-5">
                  <div className="h-11 w-11 shrink-0 rounded-full border border-forest/30 grid place-items-center font-serif text-forest">0{i+1}</div>
                  <div>
                    <div className="font-serif text-xl text-ink">{h}</div>
                    <div className="text-ink-soft text-sm mt-1 leading-relaxed">{b}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <form onSubmit={submit} className="bg-white border border-forest/10 rounded-3xl p-8 luxe-shadow self-start space-y-4" data-testid="partner-form">
            <h3 className="font-serif text-2xl text-ink">Send us your details</h3>
            <p className="text-ink-soft text-sm">Our partnerships team responds within 24 hours.</p>
            <input required value={f.name} onChange={set("name")} placeholder="Full name" className={inp} data-testid="partner-name" />
            <input required type="email" value={f.email} onChange={set("email")} placeholder="Email" className={inp} data-testid="partner-email" />
            <input required value={f.phone} onChange={set("phone")} placeholder="Phone" className={inp} data-testid="partner-phone" />
            <select value={f.inquiry_type} onChange={set("inquiry_type")} className={inp} data-testid="partner-type">
              <option value="host">I own a property</option>
              <option value="land">I own land</option>
              <option value="operator">I'm a hospitality operator</option>
              <option value="collab">Cultural collaboration</option>
            </select>
            <textarea rows={4} value={f.message} onChange={set("message")} placeholder="Tell us a bit about your property, land or work…" className={`${inp} resize-none`} data-testid="partner-message" />
            <button disabled={busy} className="w-full bg-forest text-ivory py-3.5 rounded-full font-medium text-sm hover:bg-forest-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="partner-submit">
              {busy ? "Sending…" : "Submit enquiry"} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-[11px] text-ink-soft text-center">Or call our partnerships line at <a href="tel:+919491317777" className="text-forest font-medium">+91 94913 17777</a>.</p>
          </form>
        </div>
      </section>
    </div>
  );
}

const inp = "w-full border border-forest/15 rounded-xl bg-transparent px-4 py-3 outline-none focus:border-forest text-sm text-ink";
